// START IMPORTS
// Hooks imports
import { useReport } from '@ltvco/refresh-lib/hooks';
// Theme and UI imports
import { Text, Box, Grid, Divider } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  DebugMenu,
  ReportFactory,
  FraudEmailDetails,
  FraudEmailOverview,
  FraudEmailThreatDetection,
  FraudSearchContainer,
  ReportLoading,
  SearchFormProps,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import { ReportNullState } from '@ltvco/refresh-lib/v2';
// Vendor imports
import { alpha } from '@mui/material';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { ReportRouteProps } from 'components/reports/types';

// Component imports
import {
  DetailsContainer,
  ReportContainer,
  SearchFormsContainer,
} from './components';
// Utility imports
import { getFraudSearchSubtitleText } from './utils';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface EmailFraudReportPageProps extends ReportRouteProps {
  /** Callback to handle successful search form submission */
  onSearchSuccess: SearchFormProps['onSuccess'];
  /** Callback to handle search form submission errors */
  onSearchError: SearchFormProps['onError'];
}
// END INTERFACES_AND_TYPES
export function EmailFraudReportPage({
  permalink,
  onSearchSuccess,
  onSearchError,
}: EmailFraudReportPageProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const { hash, search } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [tabState, setTabState] = useState<string>('email');
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const zeroed = isZeroed(queryResult);
  // END DERIVED_DATA_AND_UTILS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={2} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  const report = ReportFactory.create(queryResult?.data, 'email-fraud');

  if (!report) {
    return <ReportLoading menuItems={2} />;
  }

  if (report?.data?.isEmpty) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const queryParams = new URLSearchParams(search);
  const fallbackEmail = queryParams.get('email') || '';

  const {
    address,
    fraudScore,
    isSuspect,
    isShared,
    isDisposable,
    isCatchAll,
    isFrequentComplainer,
    activity,
    isLeaked,
    firstSeen,
    deliverability,
    validityScore,
    isHoneypot,
    isRecentAbuse,
    domain,
    name,
    spamTrapScore,
  } = report.data;

  const {
    trust,
    isRiskyTld,
    isDmarcRecord,
    isSpfRecord,
    isDnsValid,
    mxRecords,
    suggestedDomain,
  } = domain || {};

  const lowerCaseEmailAddress = address?.toLowerCase() ?? fallbackEmail;
  // END DATA_PREPARATION

  // START RENDER
  return (
    <>
      {/* START SEARCH_FORMS */}
      <SearchFormsContainer>
        <Text data-cy="fraud-check-form-title" variant="h2">
          FraudScan
        </Text>
        <Text
          mb={(theme) => ({ xs: theme.spacing(1), md: theme.spacing(3) })}
          data-cy="fraud-check-form-subtitle"
          variant="body2"
        >
          {getFraudSearchSubtitleText(tabState)}
        </Text>
        <FraudSearchContainer
          excludeTabs={['ip', 'url']}
          initialTab={'email'}
          onSearchSuccess={onSearchSuccess}
          onSearchError={onSearchError}
          onSearchChange={setTabState}
        />
      </SearchFormsContainer>
      {/* END SEARCH_FORMS */}

      {/* START MAIN_CONTENT */}
      <ReportContainer
        sx={{ width: 'fit-content' }}
        container
        direction={'row'}
        columns={12}
      >
        {/* START REPORT_HEADER */}
        <DetailsContainer my={3} width="100%">
          <Text data-cy="fraud-check-form-title" variant="h3" mb={1.5}>
            Email Verification
          </Text>
          <Box
            padding={1}
            mb={4}
            sx={(theme) => ({
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
            })}
          >
            <Text variant="h5" component="p" fontWeight={700} m={0}>
              Results for: {lowerCaseEmailAddress}
            </Text>
          </Box>
        </DetailsContainer>
        {/* END REPORT_HEADER */}

        {/* START FRAUD_OVERVIEW */}
        <Grid size={{ xs: 12 }} mb={4}>
          <FraudEmailOverview
            fraudScore={fraudScore}
            isValid={true}
            isSuspect={isSuspect}
          />
        </Grid>
        {/* END FRAUD_OVERVIEW */}

        <Divider sx={{ mt: 4 }} />

        {/* START THREAT_DETECTION */}
        <Grid size={{ xs: 12 }} mb={4}>
          <FraudEmailThreatDetection
            isRecentAbuse={isRecentAbuse}
            isRiskyTld={isRiskyTld}
            isHoneyPot={isHoneypot}
            spamTrapScore={spamTrapScore}
            domainTrust={trust}
          />
        </Grid>
        {/* END THREAT_DETECTION */}

        <Divider sx={{ mt: 4 }} />

        {/* START FRAUD_DETAILS */}
        <Grid size={{ xs: 12 }}>
          <FraudEmailDetails
            isDMARC={isDmarcRecord}
            isSPF={isSpfRecord}
            isDnsValid={isDnsValid}
            sMTP={String(validityScore)}
            isGenericRole={isShared}
            mxRecords={mxRecords}
            deliverability={deliverability}
            userActivity={activity}
            isDisposable={isDisposable}
            typo={suggestedDomain}
            isCatchAll={isCatchAll}
            year={firstSeen?.parsed?.year}
            month={firstSeen?.parsed?.month}
            isFrequentComplainer={isFrequentComplainer}
            firstName={name?.parsed?.first}
            isLeaked={isLeaked}
          />
        </Grid>
        {/* END FRAUD_DETAILS */}
      </ReportContainer>
      {/* END MAIN_CONTENT */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </>
  );
  // END RENDER
}
